<template>
  <subform-controller
    :form-observer="formObserver"
    :is-subform-visible="questionData.hadAnyConvictions"
    :show-confirm-modal="showConfirmModal"
    :form-items="questionData.convictions"
    :subform="FormClaimAndConviction"
    :questions="questions"
    :max-amount="constants.CONVICTIONS_MAX_QTY"
    :payload-template="convictionsFields"
    :get-rules="helpers.getConvictionsRules"
    :is-additional="isAdditional"
    item-name="conviction"
    :step-id="stepId"
    :step-index="stepIndex"
    section-item-name="convictions"
    @set-items="questionData.convictions = $event"
    @set-question="questionData.hadAnyConvictions = $event"
  >
    <template #question="{ observer }">
      <form-row
        id="convictions"
        :label="isAdditional ? titleAdditional : title"
        :error="formObserver.validated && !observer.valid"
      >
        <template
          v-if="helpText && !helpText.fullWidth"
          #helpText
        >
          <help-text-content
            :help-text="helpText"
            :is-additional="isAdditional"
          />
        </template>
        <validation-provider
          v-slot="{ failed }"
          :vid="`${stepId}_had-any-convictions`"
          name="Had Any Convictions"
          rules="required"
        >
          <radio
            v-model="questionData.hadAnyConvictions"
            :data-test="snakeCase(questionFields.hadAnyConvictions)"
            :name="`${stepId}-had-any-convictions`"
            :items="constants.YES_NO"
            :error="failed"
          />
        </validation-provider>
      </form-row>
    </template>
    <template
      #preview="{ item: { item } }"
    >
      <subform-preview
        :preview-list="formatPreviewList(item)"
        :icon="vehicleIcon"
      />
    </template>
  </subform-controller>
</template>

<script>
import { getNumberFormatted } from '@/helpers/common';

import SubformController from '@/components/SubformController/SubformController';
import FormClaimAndConviction from '@/components/Subforms/FormClaimAndConviction';
import QuestionBase from '@/components/questions/Base';
import SubformPreview from '@/components/SubformController/SubformPreview';
import useSubForm from '@/composables/useSubForm';

import { mapActions } from 'vuex';

export default {
  name: 'question-base-convictions',
  extends: QuestionBase,
  components: {
    SubformPreview,
    SubformController,
  },
  props: {
    questions: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      fields: ['hadAnyConvictions', 'convictions'],
    };
  },
  computed: {
    convictionsFields () {
      return this.questions.reduce((acc, question) => {
        const field = question.params?.customFields?.field;
        if (field) {
          acc[field] = null;
        }
        return acc;
      }, {});
    },
  },
  methods: {
    ...mapActions(['showConfirmModal']),
    formatPreviewList ({
      id,
      offenceCode,
      offenceCodeExpired,
      dateOfConviction,
      dateOfConvictionExpired,
      convictionDeclaredAccident,
      convictionDeclaredAccidentExpired,
      howMuchYouFined,
      howMuchYouFinedExpired,
      alcoholMethodTest,
      alcoholMethodTestExpired,
      alcoholLevel,
      alcoholLevelExpired,
      pointsReceived,
      pointsReceivedExpired,
      receivedBanPeriod,
      receivedBanPeriodExpired,
    }) {
      const mappings = {
        offenceCode: {
          prepend: offenceCode ? 'Reason for conviction:' : '',
          value: offenceCode ?? offenceCodeExpired,
          append: offenceCode ? '' : 'has been removed from the government’s list of convictions.<p>Please click ‘<strong>Change these details</strong>’ to add a new conviction.</p>',
          error: !offenceCode,
        },
        dateOfConviction: {
          prepend: 'Happened on',
          value: dateOfConviction ?? dateOfConvictionExpired,
        },
        convictionDeclaredAccident: {
          prepend: '',
          value: (convictionDeclaredAccident ?? convictionDeclaredAccidentExpired) ? 'Related' : 'Not related',
          append: 'to a declared accident',
        },
        howMuchYouFined: {
          prepend: 'Fined:',
          value: `£ ${getNumberFormatted(howMuchYouFined ?? howMuchYouFinedExpired)}`,
        },
        alcoholMethodTest: {
          prepend: '',
          value: this.constants.DRIVER_CONVICTION_ALCOHOL_METHOD_TEST[alcoholMethodTest ?? alcoholMethodTestExpired],
          append: 'alcohol method test used',
        },
        alcoholLevel: {
          prepend: 'Recorded alcohol level:',
          value: `${alcoholLevel ?? alcoholLevelExpired} mg/100 ml`,
        },
        pointsReceived: {
          prepend: '',
          value: `${(pointsReceived ?? pointsReceivedExpired) || 'No'} point${(pointsReceived ?? pointsReceivedExpired) === 1 ? '' : 's'}`,
          append: 'received',
        },
        receivedBanPeriod: {
          prepend: 'Received',
          value: (receivedBanPeriod ?? receivedBanPeriodExpired) > 0
            ? `${(receivedBanPeriod ?? receivedBanPeriodExpired)} month${(receivedBanPeriod ?? receivedBanPeriodExpired) > 1 ? 's' : ''}`
            : 'no',
          append: 'ban period',
        },
      };

      return this.generateClaimsConvictionsPreviewList(this.questionData.convictions, id, mappings);
    },
  },
  setup (props, context) {
    const { vehicleIcon, generateClaimsConvictionsPreviewList } = useSubForm(null, context);
    return {
      FormClaimAndConviction,

      // composable
      vehicleIcon,
      generateClaimsConvictionsPreviewList,
    };
  },
};
</script>
